import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useUserEditModalStore } from '@/stores/userEditModal';
import { updateUser } from '@/api/user';
import { getRoleList } from '@/api/role';
import { getOrganizationList } from '@/api/organization';
import type { UpdateUserRequest } from '@/DTO/user';
import type { Role } from '@/DTO/role';
import type { Organization } from '@/DTO/organization';

const { Option } = Select;

const GlobalUserEditModal: React.FC = () => {
  const { isOpen, user, closeUserEdit } = useUserEditModalStore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await getRoleList({
        offset: 0,
        limit: 1000,
        order: 'desc',
      });

      if (response.code === 0) {
        setRoles(response.data.data);
      } else {
        message.error(response.message || '获取角色列表失败');
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
      message.error('获取角色列表失败');
    } finally {
      setRolesLoading(false);
    }
  };

  // 获取组织列表
  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      const response = await getOrganizationList({
        offset: 0,
        limit: 1000,
        order: 'desc',
      });

      if (response.code === 0) {
        const activeOrganizations = response.data.data.filter((org) => org.status != 1);
        setOrganizations(activeOrganizations);
      } else {
        message.error(response.message || '获取组织列表失败');
      }
    } catch (error) {
      console.error('获取组织列表失败:', error);
      message.error('获取组织列表失败');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // 初始化表单数据
  useEffect(() => {
    if (isOpen && user) {
      fetchRoles();
      fetchOrganizations();

      const formValues = {
        username: user.username,
        email: user.email,
        phone: user.phone,
        role_ids: user.roles?.map((role) => role.id) || [],
        organization_id: user.organization_id,
        status: user.status,
        describe: user.describe,
      };
      form.setFieldsValue(formValues);
    }
  }, [isOpen, user, form]);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const values = await form.validateFields();

      const updateData: UpdateUserRequest = {
        id: user.id,
        username: values.username,
        phone: values.phone,
        role_ids: values.role_ids,
        organization_id: values.organization_id,
        status: values.status,
        describe: values.describe,
      };

      const response = await updateUser(updateData);
      if (response.code === 0) {
        message.success('用户信息更新成功');
        closeUserEdit();
        form.resetFields();
        // 这里可以添加刷新用户列表的回调
        window.location.reload(); // 简单的刷新页面，也可以用更优雅的方式
      } else {
        message.error(response.message || '更新用户失败');
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    closeUserEdit();
    form.resetFields();
  };

  return (
    <Modal title="编辑用户" open={isOpen} confirmLoading={loading} onOk={handleSubmit} onCancel={handleCancel} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item
          name="email"
          label="邮箱"
          rules={[
            { type: 'email', message: '请输入有效的邮箱地址' },
            { required: true, message: '请输入邮箱' },
          ]}
        >
          <Input placeholder="请输入邮箱地址" disabled />
        </Form.Item>

        <Form.Item name="username" label="姓名" rules={[{ min: 2, message: '姓名至少2个字符' }]}>
          <Input placeholder="请输入姓名" />
        </Form.Item>

        <Form.Item name="phone" label="电话">
          <Input placeholder="请输入电话号码" />
        </Form.Item>

        <Form.Item name="organization_id" label="所属机构" rules={[{ required: true, message: '请选择所属机构' }]}>
          <Select placeholder="请选择所属机构" loading={organizationsLoading} notFoundContent={organizationsLoading ? '加载中...' : '暂无可用机构'}>
            {organizations.map((org) => (
              <Option key={org.id} value={org.id}>
                {org.organization_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="role_ids" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
          <Select mode="multiple" placeholder="请选择角色" loading={rolesLoading} notFoundContent={rolesLoading ? '加载中...' : '暂无可用角色'}>
            {roles.map((role) => (
              <Option key={role.id} value={role.id}>
                {role.role_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
          <Select placeholder="请选择状态">
            <Option value={0}>激活</Option>
            <Option value={1}>停用</Option>
          </Select>
        </Form.Item>

        <Form.Item name="describe" label="描述">
          <Input.TextArea placeholder="请输入描述" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GlobalUserEditModal;
